import { useMemo, useState } from 'react';
import { formatRangeLabel } from "@/components/funnels/funnelListModel";
import { resolvePeriodWindow } from "@/lib/utils/periodWindow";
import { useLocation } from 'react-router-dom';
import { format, parse } from "date-fns";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useURLState } from "@/hooks/useURLState";
import { removeFilter } from "@/lib/routing/filter-dsl";
import DateDropdown from "./DateDropdown";
import { cn } from "@/lib/utils";
import { shellControlClass } from "@/components/layout/shellControl";
import FilterDropdown from "./FilterDropdown";
import WorkspaceSiteSelector from "@/components/layout/WorkspaceSiteSelector";
import { ShareButton } from "@/components/share/ShareButton";
import { HugeiconsIcon } from "@hugeicons/react"
import {
  Calendar01Icon,
  Remove01Icon,
} from "@hugeicons/core-free-icons"

const QUICK_PRESET_VALUES = new Set(["1d", "7d", "mtd"]);
const CUSTOM_TAB_VALUE = "custom";

function formatCustomPeriodLabel(period: string): string {
  if (!period.includes(":")) return "Custom";
  const [from, to] = period.split(":");
  try {
    const fromDate = parse(from, "yyyy-MM-dd", new Date());
    const toDate = parse(to, "yyyy-MM-dd", new Date());
    if (isNaN(fromDate.getTime()) || isNaN(toDate.getTime())) return "Custom";
    const currentYear = new Date().getFullYear();
    const showYear =
      fromDate.getFullYear() !== currentYear ||
      toDate.getFullYear() !== currentYear ||
      fromDate.getFullYear() !== toDate.getFullYear();
    const formatStr = showYear ? "MMM d, yy" : "MMM d";
    return `${format(fromDate, formatStr)} – ${format(toDate, formatStr)}`;
  } catch {
    return "Custom";
  }
}

/**
 * FilterBar Component - Redesigned with segmented preset dates
 *
 * Uses new IconFilter1 DSL from URL state
 * All filter changes automatically sync to URL
 * 
 * When EditorModeContext is active, shows editor controls instead of date/filter options
 */

interface FilterBarProps {
  className?: string;
  dashboardData?: any;
  filterOptionsData?: any;
  /** Render the active-filter chip row. The shell header renders it separately via <ActiveFilterChips>. */
  showChips?: boolean;
}

// Segment item (Paper): 26px tall, 10px sides, 12px; active = white thumb, 6px radius, soft drop.
const SEGMENT_CLASS =
  "h-[26px] px-2.5 rounded-md border-0 text-xs leading-4 whitespace-nowrap transition-colors " +
  "data-[state=inactive]:bg-transparent data-[state=inactive]:font-normal data-[state=inactive]:text-foreground/60 data-[state=inactive]:hover:text-foreground " +
  "data-[state=active]:bg-background data-[state=active]:font-medium data-[state=active]:text-foreground data-[state=active]:shadow-[0_1px_2px_rgb(0_0_0/0.08)] dark:data-[state=active]:bg-white/10";

const presetPeriods = [
  { value: "1d", label: "24 hours" },
  { value: "7d", label: "7 days" },
  { value: "mtd", label: "Month to date" },
];

export default function FilterBar({
  className = "",
  dashboardData,
  filterOptionsData,
  showChips = true,
}: FilterBarProps) {
  const { state, updateState } = useURLState();
  const location = useLocation();
  const isShareMode = location.pathname.startsWith('/s/');
  const [customPickerOpen, setCustomPickerOpen] = useState(false);

  const handleDateChange = (newPeriod: string) => {
    updateState({ period: newPeriod });
  };

  // Colon-separated absolute range, or any non-quick preset (30d/90d/etc.)
  const isCustomDate = state.period.includes(":");
  const isCustomTabActive = !QUICK_PRESET_VALUES.has(state.period);
  // Paper header chip names the window the preset resolves to ("Sep 19 to Sep 25").
  const presetRangeLabel = useMemo(() => {
    const w = resolvePeriodWindow(state.period);
    return (w && formatRangeLabel(w.start, w.end)) || "Select dates";
  }, [state.period]);

  // Only highlight a quick chip when period exactly matches it — never remap custom → L7.
  // While the custom picker is open, keep Custom selected so the tab doesn't snap back.
  const getToggleValue = () => {
    if (customPickerOpen || !QUICK_PRESET_VALUES.has(state.period)) {
      return CUSTOM_TAB_VALUE;
    }
    return state.period;
  };

  const handlePresetChange = (value: string) => {
    if (!value) return;
    if (value === CUSTOM_TAB_VALUE) {
      setCustomPickerOpen(true);
      return;
    }
    setCustomPickerOpen(false);
    handleDateChange(value);
  };

  // Get the current period label for mobile dropdown
  const getCurrentPeriodLabel = () => {
    const preset = presetPeriods.find(p => p.value === state.period);
    if (preset) return preset.label;
    if (isCustomDate) return formatCustomPeriodLabel(state.period);
    return "Custom";
  };

  // Normal filter bar mode
  return (
    <div className={cn("flex flex-col gap-2.5", className)}>
      {/* Controls row (Paper "App v3 · Analytics Overview" header): site · presets · dates · filter · share */}
      <div className="flex items-center gap-2 min-w-0 flex-wrap justify-end">
        {/* Website/Connector Selector */}
        <div className="min-w-0 shrink-0 mr-auto">
          <WorkspaceSiteSelector />
        </div>

        {/* Mobile: Date Period Dropdown */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button type="button" className={cn(shellControlClass, "md:hidden shrink-0")}>
              <HugeiconsIcon icon={Calendar01Icon} size={13} />
              <span className="whitespace-nowrap">{getCurrentPeriodLabel()}</span>
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="w-44">
            {presetPeriods.map((period) => (
              <DropdownMenuItem
                key={period.value}
                onClick={() => handlePresetChange(period.value)}
                className={state.period === period.value ? "bg-accent" : ""}
              >
                {period.label}
              </DropdownMenuItem>
            ))}
            <DropdownMenuItem
              onClick={() => handlePresetChange(CUSTOM_TAB_VALUE)}
              className={isCustomTabActive ? "bg-accent" : ""}
            >
              {isCustomDate ? formatCustomPeriodLabel(state.period) : "Custom"}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>

        {/* Desktop: segmented presets — #F2F2F2 track, 2px inset, white active thumb */}
        <Tabs
          value={getToggleValue()}
          onValueChange={handlePresetChange}
          className="w-auto hidden md:block shrink-0"
        >
          <TabsList className="inline-flex h-auto items-center gap-0 rounded-lg bg-[#F2F2F2] dark:bg-white/[0.06] p-0.5 border-0 shadow-none">
            {presetPeriods.map((period) => (
              <TabsTrigger key={period.value} value={period.value} className={SEGMENT_CLASS}>
                {period.label}
              </TabsTrigger>
            ))}
            <TabsTrigger
              value={CUSTOM_TAB_VALUE}
              onClick={() => setCustomPickerOpen(true)}
              className={SEGMENT_CLASS}
            >
              Custom
            </TabsTrigger>
          </TabsList>
        </Tabs>

        {/* Custom range picker — trigger visible on lg+; Custom chip opens it on all breakpoints */}
        <div className="shrink-0">
          <DateDropdown
            period={state.period}
            onDateChange={handleDateChange}
            triggerLabel={isCustomDate ? undefined : presetRangeLabel}
            customOnly={true}
            open={customPickerOpen}
            onOpenChange={setCustomPickerOpen}
            // Keep a 1px anchor below lg so the Custom chip can open the popover;
            // full "Select dates" control only from lg up.
            triggerClassName="max-lg:h-px max-lg:w-px max-lg:p-0 max-lg:overflow-hidden max-lg:opacity-0 max-lg:shadow-none"
          />
        </div>

        {/* Filters Button */}
        <div className="shrink-0">
          <FilterDropdown dashboardData={dashboardData} optionsData={filterOptionsData} showLabel={true} />
        </div>

        {/* Share */}
        {!isShareMode && (
          <div className="shrink-0">
            <ShareButton />
          </div>
        )}
      </div>

      {showChips && <ActiveFilterChips />}
    </div>
  );
}

/**
 * Active filter chips — rendered under the shell header bar (or inline in
 * FilterBar when `showChips`). Neutral chips; excludes read as struck-through.
 */
export function ActiveFilterChips({ className }: { className?: string }) {
  const { state, updateState } = useURLState();

  const handleRemoveFilter = (field: string) => {
    const newFilters = removeFilter(state.filters, field);
    updateState({ filters: newFilters });
  };

  const handleClearAll = () => {
    updateState({ filters: [] });
  };

  // Format filter for display (field:operator:value)
  const formatFilterDisplay = (filter: {
    field: string;
    operator: string;
    value: string;
  }) => {
    // Map field names to friendly labels
    const fieldLabels: Record<string, string> = {
      country: "Country",
      region: "Region",
      city: "City",
      device: "Device",
      device_type: "Device Type",
      browser: "Browser",
      os: "OS",
      referrer: "Referrer",
      utm_source: "Source",
      utm_campaign: "Campaign",
      utm_medium: "Medium",
      utm_content: "Content",
      utm_id: "ID",
      utm_term: "Term",
      page: "Page",
      entry_page: "Entry Page",
      exit_page: "Exit Page",
      date: "Date",
    };

    const label = fieldLabels[filter.field] || filter.field;
    const isExclude = filter.operator === "!=";
    const operator = filter.operator === ":" ? "" : filter.operator === "!=" ? "" : ` ${filter.operator} `;

    return {
      label,
      value: `${operator}${filter.value}`,
      isExclude,
    };
  };

  if (state.filters.length === 0) return null;

  return (
    <div className={className}>
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2 min-w-0">
          <div className="flex items-center gap-1.5 flex-wrap min-w-0">
            {state.filters.map((filter, index) => {
              const { label, value, isExclude } = formatFilterDisplay(filter);

              return (
                <span
                  key={index}
                  className="inline-flex h-6 max-w-full shrink-0 items-center gap-1 rounded-md bg-muted px-2 text-xs leading-4 text-foreground"
                >
                  {isExclude && <span className="text-foreground/60">Not</span>}
                  <span className="font-medium">{label}:</span>
                  <span className={cn("max-w-[80px] sm:max-w-[120px] truncate", isExclude && "line-through decoration-foreground/40")}>{value}</span>
                  <button
                    type="button"
                    onClick={() => handleRemoveFilter(filter.field)}
                    className="-mr-0.5 ml-0.5 shrink-0 rounded-sm text-foreground/40 transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/50"
                    aria-label={`Remove ${label} filter`}
                  >
                    <HugeiconsIcon icon={Remove01Icon} size={12} className="w-3 h-3" />
                  </button>
                </span>
              );
            })}
          </div>

          {state.filters.length > 1 && (
            <Button
              variant="ghost"
              size="sm"
              onClick={handleClearAll}
              className="text-xs text-foreground/60 hover:text-foreground font-medium h-6 px-2 shrink-0"
            >
              Clear all
            </Button>
          )}
        </div>
    </div>
  );
}
